import {
    Chip,
    DataTable,
    DataTableAction,
    DataTableCell,
    DataTableCustomComponent,
    DataTableRow,
    Page
} from '@7shifts/sous-chef';
import { __ } from 'i18n/translate';
import { Company, User } from 'adminPanel/types';
import { useCompanyUsers } from 'adminPanel/modules/Companies/hooks/useCompanyUsers';
import { useState } from 'react';
import { format, parseISO } from 'date-fns/esm';
import ImpersonateModal from 'adminPanel/modules/Companies/components/ImpersonateModal/ImpersonateModal';
import UserQueuesModal from 'adminPanel/modules/Companies/components/UserQueuesModal/UserQueuesModal';

type Props = {
    company: Company;
};
const CompanyUsersPage = ({ company }: Props) => {
    const { users, isLoading } = useCompanyUsers(company.id);
    return (
        <Page
            title={__('adminpanel.users')}
            breadcrumbs={{ label: __('adminpanel.companies'), url: '../../' }}
        >
            <DataTable
                items={users}
                isLoading={isLoading}
                itemComponent={CompanyRow}
                columns={[
                    {
                        name: 'id',
                        label: '#ID',
                        size: 0.3
                    },
                    {
                        name: 'name',
                        label: __('adminpanel.name'),
                        size: 1.6
                    },
                    {
                        name: 'email',
                        label: __('adminpanel.email'),
                        size: 2
                    },
                    {
                        name: 'queues',
                        label: 'Filas',
                        size: 0.8
                    },
                    {
                        name: 'lastLogin',
                        label: 'Último acesso',
                        size: 1.3
                    },
                    {
                        name: 'status',
                        label: __('adminpanel.status'),
                        size: 1.4
                    }
                ]}
                showActionMenu
            />
        </Page>
    );
};

type CompanyRowProps = DataTableCustomComponent<User>;
const CompanyRow = ({ item }: CompanyRowProps) => {
    const [showModal, setShowModal] = useState(false);
    const [showQueues, setShowQueues] = useState(false);
    const queueCount = item.queues?.length ?? 0;
    const actions: DataTableAction[] = [
        {
            action: 'impersonate',
            label: __('adminpanel.impersonate'),
            onAction: () => {
                setShowModal(true);
            },
            showInKebab: false
        }
    ];
    return (
        <>
            <DataTableRow actions={actions}>
                <DataTableCell columnIndex={0}>{item.id}</DataTableCell>
                <DataTableCell columnIndex={1}>{item.name}</DataTableCell>
                <DataTableCell columnIndex={2}>{item.email}</DataTableCell>
                <DataTableCell columnIndex={3}>
                    {queueCount > 0 ? (
                        <button
                            type="button"
                            onClick={() => setShowQueues(true)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: '#f1f3f5',
                                border: '1px solid #dee2e6',
                                borderRadius: 16,
                                padding: '3px 10px',
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#495057',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                }}
                            >
                                {item.queues!.slice(0, 3).map((queue, idx) => (
                                    <span
                                        key={queue.id}
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            background:
                                                queue.color || '#adb5bd',
                                            border: '1.5px solid #fff',
                                            marginLeft: idx === 0 ? 0 : -4
                                        }}
                                    />
                                ))}
                            </span>
                            {queueCount} {queueCount === 1 ? 'fila' : 'filas'}
                        </button>
                    ) : (
                        <span style={{ color: '#adb5bd', fontSize: 13 }}>
                            Sem filas
                        </span>
                    )}
                </DataTableCell>
                <DataTableCell columnIndex={4}>
                    {item.lastLogin ? (
                        <span style={{ fontSize: 13 }}>
                            {format(
                                parseISO(item.lastLogin),
                                "dd/MM/yyyy 'às' HH:mm"
                            )}
                        </span>
                    ) : (
                        <span style={{ color: '#adb5bd', fontSize: 13 }}>
                            Nunca
                        </span>
                    )}
                </DataTableCell>
                <DataTableCell columnIndex={5}>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 4,
                            alignItems: 'center'
                        }}
                    >
                        {item.online && <Chip theme="success">Online</Chip>}
                        {item.isAttendant && (
                            <Chip theme="info">
                                {__('adminpanel.attendant')}
                            </Chip>
                        )}
                        {item.onVacation && (
                            <Chip theme="warning">
                                {__('adminpanel.onVacation')}
                            </Chip>
                        )}
                        {!item.online &&
                            !item.isAttendant &&
                            !item.onVacation && (
                                <span
                                    style={{ color: '#adb5bd', fontSize: 13 }}
                                >
                                    —
                                </span>
                            )}
                    </div>
                </DataTableCell>
            </DataTableRow>
            {showModal && (
                <ImpersonateModal
                    user={item}
                    onClose={() => setShowModal(false)}
                />
            )}
            {showQueues && (
                <UserQueuesModal
                    user={item}
                    onClose={() => setShowQueues(false)}
                />
            )}
        </>
    );
};

export default CompanyUsersPage;
